/**
 * `resolve` — LA fonction du moteur, assemblée (SPEC-MOTEUR §13).
 *
 * Pure : même `(state, action, seed, content)` ⇒ même résultat, toujours. Ne mute jamais
 * `state`. Aucune I/O, aucune horloge système, aucun `Math.random` (RNG §3 uniquement).
 * Lance `InvalidAction` sans consommer ni `turn` ni tirage.
 *
 * Ce fichier ne contient **aucune règle de jeu** : il câble, dans l'ordre strict du §2.3,
 * les modules qui les portent. Chaque branchement non évident est commenté sur place — c'est
 * ici que les bugs de composition se logent (l'un d'eux, le double comptage du SEO de la
 * rente, a déjà coûté un correctif : voir `__tests__/rente-composition.test.ts`).
 */
import { actionCost, applyAction, neutraliserImage, noterPicAbonnes, piocherCartes, validateAction, } from "./actions.js";
import { InvalidAction } from "./types.js";
import { activerAxeProduitSiBesoin, noterRevenu, nuitAxeProduit, ouvrirJourneeProduit, } from "./axe-produit.js";
import { consommerProchaineProd, effetActionBoutique } from "./boutique.js";
import { H_CRISE, H_JOUR, jour } from "./clock.js";
import { verserFrontiereDeJour } from "./curves.js";
import { collectModifiers, costMods, } from "./effects.js";
import { capturerReference, resoudreChoixEvenement, resoudreReportEvenement, resoudreTrancheParManager, tirerEvenement, } from "./events.js";
import { arriveeFil, nuitFil, publicationFil } from "./fil.js";
import { appliquerGameOver, appliquerRemedeSM, majArcDeLaChute, majFaillite, majFantome, purgerStrikes, resoudreCrise, verifierGameOver, } from "./gameover.js";
import { annoncerPause, demarrerPauseAuSommeil, revelerPauseSiZoneRouge, } from "./pause.js";
import { determinerFin, finMeritee, jalonsReussis } from "./fins.js";
import { reposParDefaut, systemes } from "./menage.js";
import { effetActionBibliotheque } from "./bibliotheque.js";
import { effetActionJalon, jalonsLisibles } from "./jalons.js";
import { appliquerProgression, exigenceAtelier } from "./progress.js";
import { publier, scoresEffectifs } from "./publish.js";
import { consignesRythme, managerActif as managerActifDe, paierManager, salaireManager, tensionTresorerie, } from "./manager.js";
import { JOURS_MAX_DELEGUES, MAX_ACTIONS_DELEGUEES, RYTHME_SANS_MANAGER, appliquerReglageEquipe, instantane, noeudAchetableMaintenant, paierProducteur, plafondDelegation, premierMotifDArret, prochaineActionEquipe, producteurActif, salaireProducteur, } from "./producteur.js";
import { effetActionSkills } from "./skills.js";
import { heuresDeSilence, heuresDeSilenceFantome, nuitHook, verserRente } from "./sleep.js";
import { PALIERS } from "./types.js";
// ⟦A-16 · A-81⟧ Le récapitulatif de chaque action, relu par la délégation — jamais recalculé.
import { instantaneJauges } from "./recap.js";
import { abonnesDelegues, faitsDelegues, jaugesDeleguees, lignesParCause, videosDeleguees, } from "./delegation-recap.js";
/** Le million — objectif de la run (PLAN §1, §4.13). C'est le dernier palier « de course ». */
// ⟦A-4 §2⟧ Traits permanents par genre — la surface publique dont le client et
// `simulate.ts` ont besoin pour afficher et calibrer.
// ⟦M3.21⟧ `coutCommentaires` / `MULT_COMMENTAIRES_F` ont disparu de cette liste **avec** le
// branchement du Fil : le trait « le Fil est plus rude » se lit désormais dans
// `MULT_TOXIQUES_F` (`fil.ts`, ré-exporté plus bas). Un seul nom pour un seul effet.
export { appliquerTraitJauge, genreDe, MULT_FIDELITE_F, MULT_REPUTATION_H, MULT_SM_HORS_SOMMEIL_H, } from "./traits.js";
export const OBJECTIF_ABONNES = PALIERS[PALIERS.length - 1];
// ─────────────────────────────────────────────────────────────────────────────
// Le jeu de hooks complet
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Chaîne les `effetAction` des modules : le **premier** qui déclare avoir traité l'action
 * gagne. L'ordre importe peu (chacun ne reconnaît que ses propres `action.type`), mais il
 * est fixé pour rester déterministe si un jour deux modules revendiquaient la même action.
 */
function effetActionCombine(ctx, action) {
    // 1. Arbre de skills : acheter_skill, respec.
    if (effetActionSkills(ctx, action))
        return true;
    // 1quater. §6.3/§6.5 — Bibliothèque vivante : `remaster` et `ranger_serie`. Les deux
    //   étaient validées, facturées, et tombaient dans le `default:` de la file.
    if (effetActionBibliotheque(ctx, action))
        return true;
    // 1ter. §9.3 / M3.1 — Vidéos jalons. Le plafond de compétence ⟦A-9⟧ leur est passé par
    //   `scoresEffectifs` : un jalon n'est pas une dérogation au niveau du joueur, c'est un
    //   morceau de bravoure joué avec les mains qu'on a.
    // ⟦SPEC-FIL §2.2⟧ Le compte des jalons réussis AVANT le geste : c'est lui qui dit si la
    //   vidéo jalon a tenu (`jalons_done` ne grandit qu'en cas de réussite, `jalons.ts`), et
    //   c'est la seule information dont la Hype a besoin. Lu à froid, sans toucher `jalons.ts`.
    const jalonsAvant = ctx.state.jalons_done.length;
    if (effetActionJalon(ctx, action, (scores) => scoresEffectifs(ctx.state, scores, "longue", ctx.content).effectifs)) {
        // ⟦A-60⟧ Une vidéo jalon est une vidéo : le matos préparé y sert, et s'y consomme. Sans
        //   cette ligne, un joueur armerait un consommable, tournerait son jalon avec, et le
        //   garderait pour la vidéo d'après — l'aide brève deviendrait une aide permanente,
        //   c'est-à-dire une compétence achetée en Clicks ⟦A-50 ter⟧.
        consommerProchaineProd(ctx.state);
        // ⟦SPEC-FIL §2.2⟧ Une vidéo jalon est une sortie (+3) et, réussie, un très bon jour (+10).
        //   `video: null` — un jalon ne crée aucun `VideoRecord` (`jalons.ts`), donc aucune courbe
        //   à faire porter par la vague : il gagne de la Hype, il n'en dépense pas.
        publicationFil(ctx.state, ctx.content, null, {
            jalonReussi: ctx.state.jalons_done.length > jalonsAvant,
        });
        return true;
    }
    // 1bis. ⟦A-11 §4⟧ Boutique : acheter_objet. Placé juste après l'arbre parce que c'est la
    //   même famille de geste (dépenser pour s'améliorer), et parce que les deux lèvent leur
    //   propre `InvalidAction` depuis le hook, sur un clone que `applyAction` ne rendra pas.
    if (effetActionBoutique(ctx, action))
        return true;
    // 2. Événements : choix_evenement. On n'appelle PAS `effetActionEvenements` directement,
    //    parce qu'il jette le `cards_to_draw` du choix — et certains choix v1 en demandent une.
    if (action.type === "choix_evenement") {
        const r = resoudreChoixEvenement(ctx, action);
        if (r && r.cards_to_draw > 0) {
            piocherCartes(ctx.state, ctx.content, ctx.seed, r.cards_to_draw);
        }
        return true;
    }
    // 2bis. ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » : reporte le pending_event sur son premier
    //    choix disponible (l'issue par défaut) au lieu de laisser le joueur trancher. Même
    //    résolution que choix_evenement (`resoudreReportEvenement` l'appelle en interne), donc
    //    même traitement de `cards_to_draw`.
    if (action.type === "reporter_evenement") {
        const r = resoudreReportEvenement(ctx);
        if (r && r.cards_to_draw > 0) {
            piocherCartes(ctx.state, ctx.content, ctx.seed, r.cards_to_draw);
        }
        return true;
    }
    // 2ter. ⟦C8 · A-120⟧ « Le manager tranche » : le minuteur CLIENT a expiré sur le
    //    pending_event, ou le joueur délègue la décision. Même résolution que choix_evenement
    //    (`resoudreTrancheParManager` l'appelle en interne), donc même traitement de
    //    `cards_to_draw`. Distincte de 2bis : là c'est l'EMPLOYÉ qui choisit, pas le joueur.
    if (action.type === "trancher_par_manager") {
        const r = resoudreTrancheParManager(ctx);
        if (r && r.cards_to_draw > 0) {
            piocherCartes(ctx.state, ctx.content, ctx.seed, r.cards_to_draw);
        }
        return true;
    }
    // 3. Avenant A-2 : l'annonce de pause n'inscrit qu'une INTENTION ; la pause elle-même
    //    démarre au sommeil suivant (hook `nuit`).
    if (action.type === "annoncer_pause") {
        annoncerPause(ctx.state, ctx.events, ctx.log);
        return true;
    }
    // 4. Remèdes de SM : `pause` et `therapie` lèvent le dernier avertissement de burn-out
    //    (§11.2 — c'est ce drapeau qui porte « un remède a été pris »). Leurs coûts sont déjà
    //    débités par la table §2.2 ; il ne reste que cette conséquence-là.
    if (action.type === "pause" || action.type === "therapie") {
        appliquerRemedeSM(ctx.state);
        return false; // on laisse la file appliquer le reste de l'effet par défaut
    }
    // 5. Les deux sorties d'après-million (PLAN §4.13). Elles n'avaient AUCUN effet moteur :
    //    `actions.ts` le disait lui-même (« sans leur hook, elles n'appliquent que leur coût »),
    //    et la conséquence était que l'écran de fin — l'objectif de toute la partie — restait
    //    inatteignable. Le client a refusé d'y poser un bouton, à raison : il aurait menti.
    if (action.type === "clore_run" || action.type === "continuer_apres_rue") {
        cloreOuContinuer(ctx, action.type);
        return true;
    }
    // 6. ⟦SPEC-PRODUCTEUR §1.3 · §3.1 · §6.3⟧ La réunion d'équipe. Elle tombait dans le
    //    `default: break` de la file — typée, validée, facturée, transportée, et sans AUCUN
    //    effet : les consignes n'étaient jamais écrites, congédier ne virait personne, la prime
    //    annoncée n'était jamais débitée. Le recompte du 20/08 en a fait la première ligne de
    //    l'escalier de délégation.
    if (action.type === "regler_equipe") {
        const bascule = appliquerReglageEquipe(ctx.state, action, ctx.content);
        if (bascule.manager !== null) {
            ctx.events.push({
                kind: "warning",
                data: {
                    cause: "equipe_bascule",
                    etage: "manager",
                    geste: bascule.manager,
                    prime: bascule.prime_manager_payee,
                },
            });
            const texteManager = {
                embauche: `Le manager prend son poste. La prime de retour te coûte ${bascule.prime_manager_payee} ₵.`,
                congedie: "Tu congédies ton manager. Le rythme, c'est de nouveau toi.",
                suspendu: "Sans producteur, ton manager n'a plus rien à cadencer : il attend, sans salaire.",
                revenu: "Le producteur revient — et ton manager reprend son classeur, sans prime : il n'était jamais parti.",
            }[bascule.manager];
            ctx.log.push({ turn: ctx.state.turn, clock_h: ctx.state.clock_h, famille: "technique", text: texteManager });
            if (bascule.prime_manager_payee > 0) {
                ctx.recap?.ligne("effet_action", "argent", -bascule.prime_manager_payee, "prime de retour (manager)");
            }
        }
        if (bascule.producteur !== null) {
            ctx.events.push({
                kind: "warning",
                data: {
                    cause: "equipe_bascule",
                    etage: "producteur",
                    geste: bascule.producteur,
                    prime: bascule.prime_payee,
                },
            });
            ctx.log.push({
                turn: ctx.state.turn,
                clock_h: ctx.state.clock_h,
                famille: "technique",
                text: bascule.producteur === "congedie"
                    ? "Tu rends sa liberté à ta productrice. Plus de salaire dès demain — et tu reprends les ciseaux."
                    : `Elle revient. La prime de retour te coûte ${bascule.prime_payee} ₵, et les impayés repartent de zéro.`,
            });
            // ⟦A-16⟧ La cause, jamais le solde : la prime est une ligne, pas un trou dans le compte.
            if (bascule.prime_payee > 0) {
                ctx.recap?.ligne("effet_action", "argent", -bascule.prime_payee, "prime de retour");
            }
        }
        if (bascule.consignes_changees) {
            ctx.log.push({
                turn: ctx.state.turn,
                clock_h: ctx.state.clock_h,
                famille: "technique",
                // ⟦C60 · lot b⟧ « La ligne éditoriale » n'existe plus (l'alignement est coupé) : on parle des consignes.
                text: "Réunion d'équipe : les consignes changent. Elles s'appliquent à la prochaine vidéo.",
            });
        }
        return true;
    }
    return false;
}
/**
 * `clore_run` / `continuer_apres_rue` — les deux seules issues d'une run victorieuse.
 *
 * ⚠️ **La victoire est GRAVÉE (§9.4, PLAN §4.13)** : ni l'une ni l'autre ne touche
 * `victory_locked`, `ending`, ou quoi que ce soit que la gravure a écrit. C'est la même règle
 * qui fait qu'un game over d'après-million laisse la victoire intacte — la « mort en
 * légende » d'`appliquerGameOver`. Fermer sa run n'est pas la perdre.
 */
function cloreOuContinuer(ctx, type) {
    const { state } = ctx;
    if (type === "clore_run") {
        // Statut terminal, mais PAS `game_over` : ce n'est pas une défaite. La couche serveur
        // le mappe en `ended_reason: "victory_closed"` (SPEC-API §3.2).
        state.status = "ended";
        ctx.events.push({
            kind: "ending_chosen",
            data: { choix: "clore", ending: state.ending, subscribers: state.subscribers },
        });
        ctx.log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            famille: "chaine",
            text: "Tu refermes la chaîne sur sa plus belle page. La rue porte ton nom.",
        });
        return;
    }
    // Mode 100 % : c'est CE drapeau qui ouvre les paliers post-1M (`appliquerProgression`).
    // Tant qu'il est faux, ils n'existent pas — un joueur qui n'a pas choisi de continuer ne
    // doit pas les franchir en silence.
    state.flags.apres_la_rue = true;
    ctx.events.push({
        kind: "ending_chosen",
        data: { choix: "continuer", ending: state.ending, subscribers: state.subscribers },
    });
    ctx.log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "chaine",
        text: "Un million, et tu ne t'arrêtes pas. La suite n'est plus une course : c'est un métier.",
    });
}
/**
 * §2.3 pas 10 — progression, puis toutes les fins. Ordre voulu :
 *
 * 1. **crise de SM = 0** d'abord : elle déplace l'horloge de 224 h et les abonnés, donc tout
 *    ce qui suit doit voir l'état d'après-crise ;
 * 2. progression (niveaux, paliers, Grand Saut) ;
 * 3. strikes expirés, arc de la chute, descente vers la faillite ;
 * 4. victoire au million, **avant** le game over : la victoire est gravée et rien ne peut la
 *    reprendre (§9.4, PLAN §4.13) ;
 * 5. game over.
 */
function progressionEtFins(ctx, clockAvant) {
    const { state } = ctx;
    // ⟦C60 · A-177⟧ Les interrupteurs du ménage, lus UNE fois pour tout le pas 10.
    const sys = systemes(ctx.content);
    // 1. Crise de SM = 0 (§11.1).
    if (state.mental <= 0 && state.status === "active") {
        // « Rente evergreen versée normalement » (PLAN §4.10, l'assurance burn-out) : on la verse
        // pour la durée du blocage AVANT d'appliquer la perte. Les abonnés qu'elle apporte sont
        // donc déjà dans la base sur laquelle la perte se calcule — la déduction du §11.1 est
        // émergente, et c'est pourquoi on passe 0 en `renteAbonnes` (sinon on la compterait deux
        // fois, exactement le piège du SEO de la rente). ✎ à valider en M1.21.
        const mods = collectModifiers(state, contenuComplet(ctx.content));
        verserRente(state, H_CRISE, mods, ctx.events);
        resoudreCrise(state, 0, ctx.events);
    }
    // 2. Niveaux, paliers, Grand Saut (⟦A-1⟧).
    //    ⟦C62 · A-179⟧ Le pic d'abonnés de l'action descend jusqu'au palier du million, et à lui seul :
    //    la victoire se grave au franchissement, donc le palier qui la porte aussi.
    appliquerProgression(state, ctx.events, sys, ctx.picAbonnes ?? 0);
    // 2bis. ⟦SPEC-AXE-PRODUIT §7⟧ **L'axe Produit ↔ Authentique s'active au Grand Saut**, et
    //   jamais avant : sans argent, mesurer un ratio de dépenses serait mesurer du vide.
    //
    //   🔑 `pa_reference` est posée dans le même geste, à la valeur d'activation — c'est CE
    //   détail qui évite que l'arrivée des 600 ₵ et des premiers achats ne produise un « il a
    //   changé » absurde dès le palier 1 000. Le joueur se ferait reprocher d'avoir commencé
    //   à travailler.
    //
    //   Placé ICI plutôt que dans `progress.ts` : l'activation a besoin du CONTENU (les euros
    //   de l'arbre pour S3, l'interrupteur), et `appliquerProgression` n'en reçoit pas — le
    //   lui faire traverser rendrait la porte du Grand Saut dépendante de `skills.json`.
    //   Idempotente : elle rattrape aussi un état sérialisé avant l'axe, ou une run dont
    //   l'interrupteur vient d'être allumé.
    activerAxeProduitSiBesoin(state, ctx.content);
    // 3. Menaces.
    purgerStrikes(state);
    majArcDeLaChute(state, ctx.events, sys);
    majFaillite(state, clockAvant, ctx.events, sys);
    // ⟦C60 · A-177⟧ Sans alignement, l'image de chaîne n'existe pas : ni historique, ni cicatrice, ni
    // image. Fait ICI, au pas 10, parce que les écrivains de l'offset (pause, Fil, effets de
    // contenu) sont nombreux et que cette ligne les couvre tous, une run déjà commencée avec une
    // image abîmée la retrouve à zéro, au premier pas qui suit.
    if (!sys.alignement)
        neutraliserImage(state);
    // 4. Victoire au million (§9.4) — gravure ET détermination de la FIN.
    //
    //    ⚠️ `state.ending` n'était écrit NULLE PART, et ce n'est pas un champ décoratif : c'est
    //    la garde de `graverVictoire` (`server/src/progression.ts`). Tant qu'il valait `null`,
    //    aucun score de victoire n'était calculé, aucune ligne de classement n'était insérée et
    //    l'écran de fin du client restait vide. Voir `engine/fins.ts`.
    //
    //    La fin est LUE sur l'état de l'instant du million, avant tout ce qui suivra : c'est ce
    //    qui rend la gravure honnête (PLAN §4.13, « rien ne peut plus reprendre la victoire »).
    //
    //    ⟦C62 · lot moteur · A-179⟧ **Le million se grave AU FRANCHISSEMENT, quoi qu'il arrive ensuite.**
    //    Avant : seul le compte de la FIN de l'action comptait, et une nuit qui reprenait quelques dizaines
    //    d'abonnés entre le franchissement et ce pas 10 (posé à 1 000 000 puis 999 947, audit du 21/10)
    //    emportait la victoire sans un mot. Désormais le PIC de l'action compte aussi (`noterPicAbonnes`) :
    //    toucher le million suffit. La fin, elle, est toujours lue ICI, sur l'état du pas 10.
    //
    //    État neutre : quand les abonnés de la fin sont au million ou au-dessus, rien ne change, au
    //    caractère près (même condition, même événement, même ligne de journal). Seul le cas « franchi puis
    //    repris dans la même action » bascule, et c'était précisément la victoire perdue.
    const pic = Math.max(state.subscribers, ctx.picAbonnes ?? 0);
    if (pic >= OBJECTIF_ABONNES && !state.victory_locked) {
        state.victory_locked = true;
        if (state.status === "active")
            state.status = "won";
        state.ending = determinerFin(state, sys);
        ctx.events.push({
            kind: "victory",
            data: {
                // Ce que la chaîne comptait au franchissement : les abonnés de la fin s'ils sont au million,
                // sinon le pic (jamais le creux d'après, qui ferait écrire « LE MILLION : 999 947 abonnés »).
                subscribers: state.subscribers >= OBJECTIF_ABONNES ? state.subscribers : pic,
                jour: jour(state.clock_h),
                mode: state.mode,
                ending: state.ending,
                meritee: finMeritee(state, state.ending),
                jalons_reussis: jalonsReussis(state),
                image: state.channel_image,
            },
        });
        ctx.log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            famille: "chaine",
            text: state.ending === "la_rue"
                ? "Un million. Le village va donner ton nom à une rue."
                : state.ending === "la_communaute"
                    ? "Un million. Ce n'est pas une audience que tu as construite, c'est une communauté."
                    : "Un million. Ce n'est plus une chaîne, c'est une entreprise.",
        });
    }
    // 4bis. Avenant A-2 : la zone rouge révèle l'action `annoncer_pause` (pédagogie par le
    //   besoin). Après les clamps, donc sur la SM réellement atteinte ce tour-ci.
    revelerPauseSiZoneRouge(state, ctx.events);
    // 4ter. §9.4bis « Le Fantôme » — l'extinction silencieuse ⟦A-4 décision 1 + arbitrage F⟧.
    //   Placée **après** la victoire, comme le game over : une rue qui porte ton nom ne
    //   s'oublie pas. `heuresDeSilenceFantome` sait déjà retirer le gel d'une pause annoncée
    //   ⟦A-2⟧ ; `majFantome` télégraphie puis inscrit la fin, et le pas 5 la relit.
    //   ⟦A-130 · C12⟧ `heuresDeSilenceFantome`, pas `heuresDeSilence` : le Fantôme lit
    //   `last_activity_h`, qu'un jalon raté rompt aussi — `heuresDeSilence` reste réservé à la
    //   régularité (momentum, decay de Hype), qu'un jalon raté ne doit toujours pas nourrir.
    majFantome(state, clockAvant, heuresDeSilenceFantome(state), ctx.events);
    // 5. Game over — après la victoire, jamais avant.
    const cause = verifierGameOver(state, sys);
    if (cause && state.status !== "game_over")
        appliquerGameOver(state, cause, ctx.events);
}
/**
 * Vue du contenu attendue par `collectModifiers` (arbitrage A25). `EngineContent.skills` est
 * typé large dans `types.ts` — pour ne pas y faire dépendre `types.ts` d'`effects.ts` — donc
 * la conversion se fait ici, à l'unique frontière où les deux se rencontrent. Les schémas Zod
 * garantissent la forme au boot (`content/load.ts`), et chaque module la verrouille par une
 * assertion de compatibilité.
 */
function contenuComplet(content) {
    return {
        skills: (content.skills ?? []),
        // ⟦A-11 §4⟧ Les objets de boutique portent les mêmes `Effect` que les nœuds : leurs
        // permanents possédés sont relus ici, jamais recopiés dans l'état.
        boutique: (content.boutique ?? []),
    };
}
/**
 * Le jeu de hooks complet. `reference` porte la croissance mesurée **avant** l'action :
 * l'anti-frustration (§10.4) en dépend, parce que la file écrit `subs_at_last_sleep` au
 * pas 6, donc avant le tirage du pas 7-8 (**arbitrage A32**).
 */
export function hooksMoteur(reference, clockAvant) {
    return {
        frontiereDeJour: (ctx, jourAtteint) => {
            // ⟦SPEC-AXE-PRODUIT §2⟧ **La journée s'ouvre au journal de l'axe**, et se purge au-delà
            //   de la fenêtre de 30 jours. Avant le versement : une entrée qui existe déjà est une
            //   journée qui COMPTE comme journée sans dépense, au lieu de ne pas exister.
            //   Interrupteur éteint ⇒ sort à la première ligne, rien n'est créé.
            ouvrirJourneeProduit(ctx.state, ctx.content, jourAtteint);
            // ⟦C4⟧ Les multiplicateurs de contenu descendent jusqu'au versement. Sans eux, `rpm`,
            //   `revenus` et `xp` étaient trois cibles agrégées par `effects.ts` et lues nulle
            //   part — une démonétisation de chaîne ne coûtait rien, *L'Empire* ne rapportait rien.
            const mods = collectModifiers(ctx.state, contenuComplet(ctx.content));
            const argentAvant = ctx.state.money;
            verserFrontiereDeJour(ctx.state, ctx.events, {
                rpm: mods.multiplier.rpm,
                revenus: mods.multiplier.revenus,
                xp: mods.multiplier.xp,
            });
            // ⟦SPEC-AXE-PRODUIT §3.1⟧ Les tranches quotidiennes sont du **revenu**. Mesuré au delta
            //   de `money` plutôt que recopié depuis `verserFrontiereDeJour` : la somme versée est
            //   déjà le résultat de quatre règles (monétisation ⟦A-1⟧, RPM par format, `rpm`,
            //   `revenus`), et une seconde addition en parallèle finirait par diverger de la
            //   première. Daté de `jourAtteint`, le jour qu'on résout — pas celui d'arrivée.
            noterRevenu(ctx.state, ctx.content, ctx.state.money - argentAvant, jourAtteint);
        },
        // ⟦SPEC-PRODUCTEUR §6.2 · A-81⟧ **La paie, dans son propre hook** — même geste, même place
        //   dans la file (après les courbes, avant les charges), mais sous SA cause au
        //   récapitulatif ⟦A-16⟧ : « les salaires », plus « les vues de la journée ».
        paieEquipe: (ctx) => {
            const mods = collectModifiers(ctx.state, contenuComplet(ctx.content));
            // ⟦SPEC-PRODUCTEUR §6.2 / A-66⟧ **Le salaire du producteur, APRÈS les charges fixes.**
            //   L'ordre n'est pas indifférent : les charges de la vie ⟦A-11⟧ passent d'abord, donc
            //   c'est l'équipe qui saute en premier quand la caisse ne suit plus — jamais le loyer.
            //   ⚠️ ⟦A-81⟧ Constat, non corrigé : dans la file RÉELLE (`actions.ts` pas 5), ce hook
            //   passe AVANT le débit des charges du pas 5b — la lettre du §6.2 n'est pas tenue.
            //   L'ordre est conservé tel quel, parce que le déplacer déplacerait une partie
            //   (trajectoire d'argent) ; c'est un arbitrage d'équilibrage, pas de récapitulatif.
            //
            //   🔑 **La tension de fin de partie que demandait Mathieu, et elle a une propriété
            //   qu'aucune autre mécanique du jeu n'a : elle frappe au moment où le joueur a arrêté
            //   de regarder.** On délègue parce qu'on ne veut plus surveiller ; on doit surveiller
            //   parce qu'on a délégué.
            //
            //   ⟦A-10⟧ Producteur inactif (ou interrupteur éteint) ⇒ `paierProducteur` sort à la
            //   première ligne et n'écrit RIEN — pas même un compteur à zéro.
            const paie = paierProducteur(ctx.state, ctx.content, mods);
            if (paie !== null && paie.impaye) {
                ctx.events.push({
                    kind: "warning",
                    data: {
                        cause: "equipe_impayee",
                        etage: "producteur",
                        jours_impayes: ctx.state.counters.producteur_jours_impayes ?? 0,
                        parti: paie.parti,
                    },
                });
            }
            // ⟦§6.2 · M3.29⟧ **Le salaire du MANAGER, après celui du producteur** — deuxième
            // escalier de paie, compteur séparé, même escalier de défaut. Trois lignes fixes
            // désormais : charges (5), producteur (15), manager (25) — le cumul de 45 ₵/jour
            // que §6.1 annonçait. ⟦A-10⟧ Interrupteur éteint ou nœud absent ⇒ rien n'est écrit.
            const paieManager = paierManager(ctx.state, ctx.content, mods, producteurActif(ctx.state, mods));
            if (paieManager !== null && paieManager.impaye) {
                ctx.events.push({
                    kind: "warning",
                    data: {
                        cause: "equipe_impayee",
                        etage: "manager",
                        jours_impayes: ctx.state.counters.manager_jours_impayes ?? 0,
                        parti: paieManager.parti,
                    },
                });
            }
        },
        publier: (ctx, action) => {
            // `clockAvant` : le marché des freelances consulté à la publication est celui du DÉBUT
            // de l'action — la fenêtre que le joueur a vue et que la validation a acceptée
            // (`ContexteEmbauche.clock_marche`). Une longue traverse une frontière de fenêtre
            // presque une fois sur deux ; sans l'épinglage, le devis affiché n'était pas le débité.
            const video = publier(ctx, action, clockAvant);
            // ⟦SPEC-FIL §2.2 · §2.3 B / M3.21⟧ **Le pas de publication du Fil.** Après `publier`, et
            //   pas dedans : la vague ne fait que remplacer la première tranche d'une courbe déjà
            //   construite (rien ne l'a encore consommée — le versement est au pas 5a du tour
            //   suivant), et le gain de Hype ne dépend que de ce que la publication a produit. Le
            //   cœur calibré dont dépendent les six cibles de CI ⟦A-40⟧ reste donc intouché.
            //
            //   La viralité se lit dans les événements du tour : `publier` y pousse un `kind:
            //   "viral"` quand le jet passe, et c'est déjà le signal que le reste du moteur écoute
            //   (§10.2). Le relire ainsi évite de faire remonter un booléen à travers `publier`.
            const pubFil = publicationFil(ctx.state, ctx.content, video, {
                viral: ctx.events.some((e) => e.kind === "viral"),
            });
            // ⟦SPEC-FIL §9.4 · A-82⟧ **La vague se voit.** `publicationFil` rendait `hype_vague` et
            //   cette valeur était jetée : les −15 se fondaient dans « la mise en ligne », et le
            //   joueur ne savait jamais que sa communauté avait dépensé sa Hype pour le partager.
            //   Le gain de publication (+3/+20/+10), lui, reste à la cause `publication` — c'est bien
            //   la mise en ligne qui le produit. `ligne()` ignore un delta nul : sans vague, rien.
            ctx.recap?.ligne("fil", "hype", pubFil.hype_vague, "ta communauté a partagé");
        },
        nuit: (ctx, deltaH) => {
            // Avenant A-2 point 4 : la pause annoncée démarre au sommeil suivant l'annonce — AVANT
            // la résolution de nuit, pour que cette nuit-là bénéficie déjà du gel du silence et de
            // la récupération SM ×1,5 que `sleep.ts` applique.
            // ⟦SPEC-FIL §2.5⟧ `ctx.content` : c'est lui qui décide si l'abus de pause coûte de la
            // Hype (Fil allumé) ou seulement l'image et la Fidélité d'⟦A-2⟧ (Fil éteint).
            demarrerPauseAuSommeil(ctx.state, ctx.events, ctx.log, ctx.content);
            // ⟦SPEC-AXE-PRODUIT §3.1⟧ Rente evergreen et merch sont du **revenu**. Même mesure au
            //   delta que les tranches quotidiennes, et pour la même raison.
            const argentAvantNuit = ctx.state.money;
            nuitHook({
                state: ctx.state,
                content: ctx.content,
                seed: ctx.seed,
                events: ctx.events,
                log: ctx.log,
                // ⟦A-16⟧ Le collecteur descend jusqu'à la nuit : sans lui, rente, désabonnements et
                // decay de momentum se fondraient dans une seule ligne « nuit ».
                recap: ctx.recap,
                // ⟦C62 · A-179⟧ Le pic d'abonnés descend lui aussi : la rente peut faire franchir le million,
                // et les désabonnés de la même nuit le reprendre.
                noterPic: () => noterPicAbonnes(ctx),
            }, deltaH);
            // ⟦SPEC-FIL §2.4 · §3.4 / M3.21⟧ **Le pas de nuit du Fil** : la Hype redescend (la
            //   cinquième pression d'⟦A-15⟧ — le monde continue sans toi) et la file vieillit.
            //   Aucun `draw` : le seul aléa du Fil est l'arrivée, et elle se joue au pas 6bis.
            //   `heuresDeSilence` retire déjà le gel d'une pause annoncée ⟦A-2⟧ — la même lecture
            //   que le momentum et l'érosion de Fidélité, pour que les cinq pressions comptent le
            //   silence de la même façon.
            noterRevenu(ctx.state, ctx.content, ctx.state.money - argentAvantNuit);
            const nuitDuFil = nuitFil(ctx.state, ctx.content, deltaH, heuresDeSilence(ctx.state));
            // ⟦SPEC-FIL §9.4 · A-16⟧ **Une pression qu'on ne voit pas n'existe pas.** La Hype est la
            //   cinquième pression du temps ⟦A-15⟧ : sa décroissance DOIT apparaître dans le
            //   récapitulatif de toute action qui traverse une nuit, exactement comme le decay de
            //   momentum. Les lignes sont posées avec `ligne()` et non mesurées : deux causes
            //   touchent `hype` dans la même nuit (décroissance, puis rien d'autre pour l'instant) et
            //   un instantané ne saurait pas les distinguer.
            //
            //   ⚠️ Les trois lignes ne coûtent rien à `hype = 0` : `ligne()` ignore un delta nul, et
            //   une nuit sans Fil n'a ni sortants ni malus. L'état neutre est gratuit ⟦A-10⟧.
            ctx.recap?.ligne("fil", "hype", nuitDuFil.hype.delta, "silence");
            ctx.recap?.ligne("fil", "fidelite", nuitDuFil.abandon, "ton Fil déborde, tu ne le lis plus");
            if (nuitDuFil.sortis.length > 0) {
                const n = nuitDuFil.sortis.length;
                ctx.recap?.fait("fil", `${n} commentaire${n > 1 ? "s ont" : " a"} défilé — tu ne l${n > 1 ? "es" : "'"}as pas lu${n > 1 ? "s" : ""}.`);
            }
            // ⟦SPEC-FIL §9.5 · A-82⟧ **Le community manager se voit** : son salaire est une ligne, et
            //   chaque commentaire qu'il a traité a la sienne — détaillée par l'id, comme les tiennes.
            //   Éteint, ou sans le nœud, `nuitCM` rend un rapport vide et `ligne()` n'écrit rien.
            const cm = nuitDuFil.cm;
            ctx.recap?.ligne("fil", "argent", -cm.cout, "le community manager");
            for (const t of cm.traites) {
                ctx.recap?.ligne("fil", "fidelite", t.fidelite, t.comment_id);
                ctx.recap?.ligne("fil", "hype", t.hype, t.comment_id);
            }
            if (cm.traites.length > 0 || cm.impaye) {
                const n = cm.traites.length;
                ctx.log.push({
                    turn: ctx.state.turn,
                    clock_h: ctx.state.clock_h,
                    text: cm.impaye
                        ? "Ton community manager n'a pas été payé cette nuit : il n'a rien fait. Arrête-le depuis le Fil si la caisse ne suit plus."
                        : `Ton community manager a répondu à ${n} commentaire${n > 1 ? "s" : ""} pendant la nuit.`,
                });
            }
            // ⟦SPEC-AXE-PRODUIT §2 · §4.4 · §5⟧ **Le pas de nuit de l'axe Produit ↔ Authentique** :
            //   les drapeaux échus retombent, l'axe glisse d'un quart de son écart vers `P_cible`,
            //   et la dérive rapide est détectée par PAS de 14 jours simulés.
            //
            //   ⚠️ **Écart de numérotation signalé.** SPEC-AXE-PRODUIT §2 le place en « nouveau
            //   pas 6bis » de SPEC-MOTEUR §2.4 — mais 6bis est déjà pris par l'arrivée du Fil
            //   ⟦SPEC-FIL §3.1⟧, livrée avant. La position RÉELLE demandée est tenue, et c'est elle
            //   qui compte : à chaque résolution de nuit, **après** que la rente de la nuit soit au
            //   journal (sans quoi le revenu du jour manquerait au dénominateur de S1) et avant le
            //   tirage d'événement du pas 7-8, pour qu'une dérive posée ce soir puisse ouvrir un
            //   « il a changé » dès ce tour-ci.
            //
            //   Aucun `draw` : l'axe est une lecture de l'état, pas un jet (§0).
            nuitAxeProduit(ctx.state, ctx.content, ctx.events);
        },
        // ⟦SPEC-FIL §3.1 / M3.21⟧ Pas 6bis — les commentaires arrivent. `arriveeFil` lit la vidéo
        //   du tour dans l'état (classe, format) : rien à recopier ici, donc rien à recopier faux.
        filArrivee: (ctx, moment) => {
            const { decouverte, sortis } = arriveeFil(ctx.state, ctx.content, ctx.seed, moment, {
                viral: ctx.events.some((e) => e.kind === "viral"),
                // ⟦A-130 · C12⟧ `ContexteArrivee.silenceH` « sert uniquement à la voie « jamais
                //   décollé » du Fantôme » (doc de `fil.ts`) : même bascule que `majFantome` plus haut.
                silenceH: heuresDeSilenceFantome(ctx.state),
            });
            // ⟦§3.4 · A-82⟧ L'arrivée a débordé : les plus anciens sont sortis — même traitement que
            //   la péremption, donc la même ligne narrative, et aucun malus.
            if (sortis.length > 0) {
                const n = sortis.length;
                ctx.recap?.fait("fil", `${n} commentaire${n > 1 ? "s ont" : " a"} défilé — le Fil débordait, tu ne l${n > 1 ? "es" : "'"}as pas lu${n > 1 ? "s" : ""}.`);
            }
            // ⟦§9.3 · A-9⟧ Le Fil se découvre à l'arrivée de son PREMIER commentaire — pas à un
            //   étage d'escalier ⟦A-5⟧, qu'on ne crée pas pour lui. Le mode découverte fait le reste.
            if (decouverte) {
                ctx.events.push({ kind: "warning", data: { decouverte: "fil" } });
                ctx.log.push({
                    turn: ctx.state.turn,
                    clock_h: ctx.state.clock_h,
                    famille: "quotidien",
                    text: "Quelqu'un a commenté. Ta chaîne vient de gagner une section commentaires.",
                });
            }
        },
        tirerEvenement: (ctx, moment) => {
            tirerEvenement(ctx, moment, { reference });
        },
        progression: (ctx) => {
            progressionEtFins(ctx, clockAvant);
        },
        effetAction: effetActionCombine,
        // ⟦A-11 §1⟧ **Le câblage qui manquait.** `costMods` existait depuis M1.8 et n'avait
        // aucun appelant de production : tout nœud censé réduire le coût d'une action ne faisait
        // rien (`Le Setup` ×0,9, `Le plateau` ×0,8, `Réponses aux commentaires` ×0,5 sur la SM).
        // C'est ici, et seulement ici, que le moteur les branche — sur l'état d'AVANT l'action.
        coutMods: (state, action, content) => costMods(collectModifiers(state, contenuComplet(content)), action),
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// §13 La signature générale
// ─────────────────────────────────────────────────────────────────────────────
/**
 * LA fonction du moteur. Pure : même `(state, action, seed, content)` ⇒ même résultat.
 *
 * - Ne mute jamais `state` (retourne un nouvel objet).
 * - Aucune I/O, aucune horloge système, aucun `Math.random`.
 * - Lance `InvalidAction` (avec un `InvalidActionCode`) **sans** consommer ni `turn` ni tirage.
 *
 * Le 4e paramètre `content` est l'**arbitrage A25** : ni SPEC-MOTEUR §13 ni SPEC-API §7.1 ne
 * le passait, alors que le moteur ne peut ni publier sans l'alignement du sujet ni tirer un
 * événement sans `events.json`. Il est explicite — aucun singleton, la pureté est préservée.
 */
export function resolve(state, action, seed, content) {
    // ⟦SPEC-PRODUCTEUR §5.2 / A-66⟧ **La délégation se traite ICI et pas dans `applyAction`**,
    // et ce n'est pas un détail d'organisation : chaque action jouée par l'équipe a besoin de
    // ses PROPRES hooks (`capturerReference` et `clockAvant` sont capturés par action, et
    // l'anti-frustration §10.4 en dépend). La traiter dans `applyAction` lui aurait fait rejouer
    // vingt actions sous la référence de la première — c'est-à-dire fausser ⟦A32⟧ vingt fois.
    if (action.type === "laisser_tourner")
        return laisserTourner(state, action, seed, content);
    // Capturées AVANT toute résolution : la file écrase ces deux repères en cours de route.
    const reference = capturerReference(state);
    const clockAvant = state.clock_h;
    return applyAction(state, action, seed, content, hooksMoteur(reference, clockAvant));
}
/**
 * ⟦SPEC-PRODUCTEUR §5.2 / A-66⟧ **Le jeu tourne jusqu'à ce qu'il ait besoin du joueur.**
 *
 * 🔑 **Le critère de conception qui gouverne cette boucle, et il n'est pas négociable ⟦§5.1⟧ :
 * toute interruption doit appeler une décision.** Une interruption qui se résout en cliquant
 * « continuer » est un défaut de conception, pas un réglage de confort — elle remplace une
 * corvée par une attente, ce qui est pire. C'est aussi ce qui interdit les interruptions
 * « informatives » (« une tendance est morte », « ton rival a publié ») : elles ne demandent
 * rien, donc elles n'ont pas le droit d'exister. Ces faits vont au récapitulatif.
 *
 * ⚠️ **Rien ici n'automatise la fenêtre d'événement.** `evenement` est le premier motif testé
 * et il n'est absorbable par personne — c'est la décision de Mathieu, et c'est ce qui reste
 * à jouer quand tout le reste est délégué.
 *
 * **Chaque action passe par `resolve`, le même chemin que le joueur** : aucune formule n'est
 * dupliquée, donc aucune ancre de calibrage ne peut bouger. La délégation déplace *quelles*
 * actions sont jouées, jamais *ce que vaut* une action.
 */
function laisserTourner(state, action, seed, content) {
    // La validation d'abord — un refus ne consomme ni `turn` ni tirage (SPEC-MOTEUR §2.2).
    validateAction(state, action, content, undefined, seed);
    const plafondH = plafondDelegation(state, action.jusqu_a_jours);
    const abonnesAvant = state.subscribers;
    const argentAvant = state.money;
    const duH = state.clock_h;
    let courant = state;
    let evenements = [];
    let journal = [];
    let actions = 0;
    let videos = 0;
    let salaires = 0;
    const joursDetail = [];
    // ⟦§1.4 / §5.5 · A-81⟧ La matière du récapitulatif complet — tout est OBSERVÉ, rien n'est
    // recalculé : les lignes ⟦A-16⟧ de chaque action jouée (elles étaient jetées), l'instantané
    // des jauges au départ (la MÊME fonction que les lignes), le dernier id de vidéo d'avant.
    const lignesRecap = [];
    const jaugesAvant = instantaneJauges(state);
    const idVideoAvant = state.videos.reduce((max, v) => Math.max(max, v.id), 0);
    /** ⟦§5.4⟧ `palier` : l'action qui vient d'être jouée a franchi un palier (`milestone`). */
    let palierFranchi = false;
    /** ⟦§5.4 · A-92⟧ `peril` : l'action qui vient d'être jouée a OUVERT l'arc de la chute. */
    let arcOuvert = false;
    let dernierJour = -1;
    /** Une ligne par jour simulé — le matériau des « compteurs qui montent » (§5.5). */
    const noterJour = (videosDuJour) => {
        const j = Math.floor(courant.clock_h / H_JOUR);
        if (j === dernierJour || joursDetail.length >= JOURS_MAX_DELEGUES)
            return;
        dernierJour = j;
        joursDetail.push(instantane(courant, videosDuJour));
    };
    noterJour(0);
    let arret = "fenetre";
    while (courant.clock_h < plafondH) {
        const mods = collectModifiers(courant, contenuComplet(content));
        // ⟦§5.3 / §5.4bis · M3.29⟧ **Le contexte de rythme** — ce que le MANAGER change. Sans lui
        // (interrupteur, nœud, congédiement, producteur inactif) : les défauts d'⟦A-66⟧.
        const manActif = managerActifDe(courant, content, mods, producteurActif(courant, mods));
        const rythmeConsignes = consignesRythme(courant.consignes, manActif, reposParDefaut(content));
        const rythme = manActif
            ? {
                manager: true,
                cadence: rythmeConsignes.cadence,
                repos: rythmeConsignes.repos,
                tension: tensionTresorerie(courant, content),
            }
            : RYTHME_SANS_MANAGER;
        // ⟦§5.4⟧ Les deux signalements « une fois par objet » — voir `counters.equipe_signales`.
        const dejaSignales = new Set(courant.counters.equipe_signales ?? []);
        const jalon = jalonsLisibles(courant, content).find((j) => j.jouable && !dejaSignales.has(j.id));
        // ⟦§3.3 / §5.4 · M3.29⟧ **`noeud_vise` affine l'interruption `competence`** : posé (et
        // manager actif), on n'interrompt QUE pour ce nœud-là. La seule passerelle entre la
        // délégation et l'arbre, à sens unique : il signale, il n'achète jamais.
        const skillsPourNoeud = (content.skills ?? []);
        const vise = manActif ? rythmeConsignes.noeud_vise : null;
        const noeud = noeudAchetableMaintenant(courant, vise === null ? skillsPourNoeud : skillsPourNoeud.filter((sk) => sk.id === vise));
        const noeudNeuf = noeud !== null && !dejaSignales.has(noeud) ? noeud : null;
        const motif = premierMotifDArret(courant, content, mods, jalon !== undefined, noeudNeuf !== null, rythme, palierFranchi, arcOuvert);
        if (motif !== null) {
            arret = motif;
            // On note ce qu'on vient de montrer, pour que la relance immédiate progresse (§5.4).
            if (motif === "jalon" && jalon !== undefined) {
                courant = marquerSignale(courant, jalon.id);
            }
            else if (motif === "competence" && noeudNeuf !== null) {
                courant = marquerSignale(courant, noeudNeuf);
            }
            break;
        }
        if (actions >= MAX_ACTIONS_DELEGUEES) {
            // ⚠️ ⟦§5.4⟧ Si `borne` se déclenche en pratique, c'est un **défaut d'équilibrage à
            // corriger**, pas un comportement à documenter : elle ne demande aucune décision, donc
            // elle viole le critère du §5.1. C'est ce que T-PROD-17 est censé compter.
            arret = "borne";
            break;
        }
        // ⟦§5.3⟧ Le plancher d'énergie est « coût + marge » — le coût RÉEL de l'action envisagée,
        // mods compris, plus jamais un zéro en dur (recompte du 20/08 : la marge était appliquée
        // à côté, plancher 20 au lieu de « coût + 20 »).
        const tour = prochaineActionEquipe(courant, content, mods, seed, (a) => Math.max(0, -actionCost(a, courant, costMods(mods, a)).energy_delta), rythme);
        if (tour.type === "arret") {
            // ⟦§4.5 / §5.4⟧ `delegation_impayable` : il voulait produire, une étape au moins ne
            // peut pas être payée — la règle des ciseaux dit « pas de vidéo », et le motif est dit
            // au joueur au lieu d'être découvert 120 veilles plus tard sous `borne`.
            arret = tour.motif;
            break;
        }
        const suivante = tour.action;
        const courantAvant = courant.clock_h;
        let pas;
        try {
            pas = resolve(courant, suivante, seed, content);
        }
        catch (e) {
            // ⟦§5.2⟧ Une action refusée n'est pas une exception à propager — mais elle n'est plus
            // non plus « toujours la trésorerie » : ce déguisement affichait un motif FAUX au
            // joueur (recompte du 20/08). Depuis que le critère 0 élimine en amont (thème épuisé,
            // format verrouillé, budget), un refus ici est un trou d'élimination : on ne le
            // maquille qu'en ce qu'il est. L'argent rend `tresorerie` ; tout le reste rend
            // `borne` — le motif que §5.4 définit comme « un défaut d'équilibrage à corriger »,
            // celui que T-PROD-17 compte.
            const code = e instanceof InvalidAction ? e.code : null;
            arret = code === "not_enough_money" || code === "delegation_impayable" ? "tresorerie" : "borne";
            break;
        }
        courant = pas.state;
        evenements = [...evenements, ...pas.events];
        journal = [...journal, ...pas.log];
        // ⟦A-16 · A-81⟧ La cause, jamais le solde — les lignes de CETTE action, gardées.
        if (pas.recap)
            lignesRecap.push(...pas.recap.lignes);
        // ⟦§5.4 · A-81⟧ Un palier franchi par cette action arrête la boucle au tour suivant — avant
        // `competence`, après `fin` et `evenement`, l'ordre de la table.
        palierFranchi = pas.events.some((e) => e.kind === "milestone");
        // ⟦§5.4 · A-92⟧ L'arc de la chute OUVERT par cette action arrête la boucle au tour suivant
        // (`peril`) — le fait, pas l'état : voir `premierMotifDArret`.
        arcOuvert = pas.events.some((e) => e.kind === "warning" && e.data.cause === "arc_chute_ouvert");
        actions += 1;
        const publiees = pas.events.filter((e) => e.kind === "video_published").length;
        videos += publiees;
        // ⟦A-16⟧ **La cause, jamais le solde.** Les salaires sont comptés par DIFFÉRENCE de
        // compteur — jours de frontière traversés moins jours impayés — plutôt qu'additionnés en
        // parallèle du versement. Une seconde addition qui longe la première finit toujours par
        // en diverger : c'est exactement la leçon de `noterRevenu`, qui mesure au delta de `money`
        // pour la même raison.
        const joursTraverses = Math.floor(pas.state.clock_h / H_JOUR) - Math.floor(courantAvant / H_JOUR);
        const impayesProducteur = pas.events.filter((e) => e.kind === "warning" &&
            e.data.cause === "equipe_impayee" &&
            e.data.etage === "producteur").length;
        const impayesManager = pas.events.filter((e) => e.kind === "warning" && e.data.cause === "equipe_impayee" && e.data.etage === "manager").length;
        salaires += salaireProducteur(courant, content) * Math.max(0, joursTraverses - impayesProducteur);
        // ⟦§6.1 · M3.29⟧ La troisième facture : le salaire du manager, quand il travaille.
        if (rythme.manager) {
            salaires += salaireManager(courant, content) * Math.max(0, joursTraverses - impayesManager);
        }
        noterJour(publiees);
    }
    // ⟦§5.4 · A-81⟧ La fenêtre s'est refermée SUR un palier : « placer les +2 PC » est la
    // décision la plus utile à annoncer, et la table la place loin devant `fenetre`. Seuls
    // `fin` et `evenement` passent avant — eux, la relance les rend aussitôt.
    if (arret === "fenetre" && palierFranchi && courant.status === "active" && courant.pending_event === null) {
        arret = "palier";
    }
    // ⟦§5.2 / §1.5⟧ **L'arrêt s'annonce en `warning` — aucun nouveau `EngineEvent.kind`.** Le
    // pseudocode de la spec l'exige (`warning{ arret: motif }`) et le recompte du 20/08 a
    // trouvé le motif voyageant dans `delegation.arret` seulement, lu par le seul écran
    // Équipe : un joueur ailleurs n'était jamais prévenu. Le canal `warning` est celui que la
    // télégraphie utilise déjà (SPEC-MOTEUR §1.8) — le journal et les toasts en héritent.
    evenements = [...evenements, { kind: "warning", data: { arret } }];
    return {
        state: courant,
        events: evenements,
        log: journal,
        delegation: {
            du_h: duH,
            au_h: courant.clock_h,
            jours: Math.round((courant.clock_h - duH) / H_JOUR),
            actions,
            arret,
            jours_detail: joursDetail,
            videos,
            abonnes: { avant: abonnesAvant, apres: courant.subscribers, ...abonnesDelegues(lignesRecap) },
            argent: { avant: Math.round(argentAvant), apres: Math.round(courant.money) },
            salaires: Math.round(salaires),
            // ⟦§1.4 / §5.5 · A-81⟧ Le récapitulatif complet — voir `delegation-recap.ts`.
            jauges: jaugesDeleguees(lignesRecap, jaugesAvant, instantaneJauges(courant)),
            argent_lignes: lignesParCause(lignesRecap, "argent"),
            faits: faitsDelegues(evenements),
            videos_detail: videosDeleguees(courant, idVideoAvant),
        },
    };
}
/** Ajoute un id à `counters.equipe_signales` sans muter l'état d'entrée. */
function marquerSignale(state, id) {
    const deja = state.counters.equipe_signales ?? [];
    if (deja.includes(id))
        return state;
    return { ...state, counters: { ...state.counters, equipe_signales: [...deja, id] } };
}
// ─────────────────────────────────────────────────────────────────────────────
// §3.4 Patterns d'Atelier — stubs assumés (la forme complète est serveur, M2.8)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Ce que le moteur sait d'un pattern : l'**exigence** au palier courant (§9.2). La forme
 * complète des 5 patterns (fenêtres, cuts, trajectoires, heatmap) est un objet **serveur**
 * (SPEC-API §6.2, tâche M2.8) — et l'avenant **A-3** en a refondu trois. Ce stub est donc
 * volontairement mince : il ne prétend pas générer un pattern jouable.
 */
export function generateAtelierPattern(state, stage, seed) {
    return {
        stage,
        requirement: exigenceAtelier(state),
        seed_component: `${seed}:${state.turn}:${stage}`,
    };
}
/**
 * Reconstruction du score d'une timeline (§3.4). **Stub** : le barème détaillé par mini-jeu
 * appartient à SPEC-UI §5 et son implémentation autoritative à `server/src/atelier/validate.ts`
 * (**arbitrage A13**, tâche M2.9). Le moteur n'applique ici que la borne qu'il possède : le
 * plafond de score du mode tolérance.
 *
 * ⟦A-33⟧ **Ce plafonnement-ci reste une COUPE FRANCHE, délibérément**, et c'est le seul des
 * deux dans ce cas. Il ne mesure pas ce qu'une chaîne tire d'un geste : il vérifie qu'un score
 * *envoyé par le client* tient dans l'échelle. Deux raisons de ne pas le compresser :
 *
 *  - `pattern.requirement` vient de `generateAtelierPattern`, qui appelle `exigenceAtelier`
 *    **sans `stage`** : son `score_cap` ne porte donc **jamais** le plafond de compétence,
 *    seulement la borne d'échelle (100, ou 85 en tolérance). Il n'y a rien à compresser ici.
 *  - Une borne anti-triche doit être **dure** : un 140 envoyé par un client trafiqué doit
 *    valoir 100, jamais `100 + 40 × K`. Le geste appartient au joueur, l'échelle au moteur.
 *
 * Le vrai site du plafond de compétence est `scoresEffectifs` (`publish.ts`), et lui compresse.
 */
export function validateAtelierScore(pattern, scoreClient) {
    return Math.min(Math.max(0, scoreClient), pattern.requirement.score_cap);
}
/** Ré-export : l'exigence d'Atelier au palier courant (§9.2). */
export { exigenceAtelier } from "./progress.js";
// ─────────────────────────────────────────────────────────────────────────────
// Surface publique du moteur
// ─────────────────────────────────────────────────────────────────────────────
//
// **Curée, pas déversée.** Trois consommateurs en dépendent — le serveur (autoritatif), le
// client (previews non autoritatives, affichage des coûts et des états) et `scripts/`
// (`play.ts`, `simulate.ts`). Ce qui n'est pas ici les oblige à recopier des constantes, et
// une constante recopiée dérive : `simulate.ts` avait dû redéfinir `H_JOUR = 16` et les
// seuils de zone SM faute de les trouver.
//
// Deux modules ne sont PAS ré-exportés en bloc et c'est délibéré : `publish` et `trends`
// exportent tous deux `potentielAccroche`, et `publish`/`sleep` tous deux
// `RENTE_MULT_SERIE` — duplications réelles, inscrites en **A40**. M2.10 demandait de les
// dédupliquer « dans la même passe » que le marché des freelances ; **ce n'est pas fait**, et
// c'est délibéré : `potentielAccroche` est au cœur de la formule d'Accroche, que les cibles
// bloquantes de la CI mesurent. Le mélanger à l'ajout d'une mécanique rendrait tout écart de
// campagne inattribuable — on ne saurait plus lequel des deux a bougé. À faire seul, mesuré
// seul. En attendant, on expose la version canonique.
// — Horloge : toutes les conversions du §2.1, pour que personne ne réécrive `/ 16` —
export * from "./clock.js";
/**
 * ⟦SPEC-PRODUCTEUR / A-66⟧ — **Le producteur, deuxième étage de l'escalier de délégation.**
 *
 * Cette ligne n'est pas décorative : un module qu'on n'exporte pas est un module qui n'existe
 * pas ⟦M3.21⟧. Le client en a besoin pour l'écran de réglage (`CONSIGNES_DEFAUT`,
 * `producteurActif`, `equipeActive`), le serveur pour valider, et `simulate.ts` pour mesurer.
 */
export * from "./producteur.js";
/**
 * ⟦SPEC-PRODUCTEUR §5 · M3.29⟧ — **Le manager, troisième étage de l'escalier.** Même règle que
 * le producteur : un module qu'on n'exporte pas est un module qui n'existe pas.
 */
export * from "./manager.js";
// — RNG : `draw` et l'enum fermé des purposes (§3.2) —
export * from "./rng.js";
// ⟦SPEC-AXE-PRODUIT / A-62⟧ — L'axe Produit ↔ Authentique : ses constantes ✎, ses lectures
//   (`axeProduit`, `bandeDeLaChaine`, `lectureAxeProduit`) et ses points d'entrée. Le client
//   en a besoin pour AFFICHER la bande ⟦§9.2 question 1⟧ ; sans cette ligne, la mécanique
//   vivrait dans le moteur et nulle part ailleurs — le maillon « écran » de
//   `docs/AUDIT-MAILLONS.md`, celui qui manque le plus souvent.
export * from "./axe-produit.js";
// — Actions : état initial, table des coûts, validation, bandes d'alignement —
export * from "./actions.js";
// — Formule : le helper pur §12 et ses composantes (Découverte, Portée, TC, malus) —
export * from "./formula.js";
// — Courbes, progression, effets de contenu —
export * from "./curves.js";
export * from "./progress.js";
export * from "./effects.js";
// — Escalier de déverrouillage ⟦A-5⟧ —
export * from "./escalier.js";
// — Les trois fins de victoire (§9.4) —
export * from "./fins.js";
// — Vidéos jalons (§9.3, M3.1) —
export * from "./jalons.js";
// ⟦C60 · A-177⟧ Le grand ménage : les interrupteurs, lus en un seul endroit.
export * from "./menage.js";
// — Bibliothèque vivante : remaster et séries (§6.3, §6.5) —
export * from "./bibliotheque.js";
// — Repos : dépassement d'heures et nuits contenues ⟦A-14 §2⟧ —
export * from "./repos.js";
// — Surcharge d'énergie : grasse matinée et consommables ⟦A-20⟧ —
export * from "./surcharge.js";
// — Idées : péremption des cartes d'inspiration ⟦A-15 §1⟧ —
export * from "./idees.js";
// — Récapitulatif des jours écoulés ⟦A-16⟧ —
export * from "./recap.js";
// — Récapitulatif complet d'une délégation ⟦SPEC-PRODUCTEUR §1.4 / §5.5 · A-81⟧ —
export * from "./delegation-recap.js";
// — Opportunités qui expirent ⟦A-15 §4⟧ —
export * from "./opportunites.js";
// — Créateurs PNJ ⟦A-15 §3 / M3.16⟧ —
export * from "./rivaux.js";
// ⟦SPEC-FIL · A-57 / M3.21⟧ **Le Fil et la Hype.** Cette ligne manquait, et son absence
// résumait le problème : `fil.ts` faisait 795 lignes et 51 tests, et **aucun des trois
// consommateurs du moteur ne pouvait même en importer une constante**. Le client en a besoin
// pour l'écran (M3.23) et pour dire le trait genré à la création de run, `simulate.ts` pour
// mesurer. Un module qu'on n'exporte pas est un module qui n'existe pas.
export * from "./fil.js";
// ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **La niche de chaîne.** Exportée en entier, et ce n'est pas une
// formalité : le maillon « écran » de cette mécanique EST une lecture (`nicheReconnue`,
// `libelleNiche`) que le HUD du Studio appelle directement. Sans cette ligne, le bandeau
// n'aurait aucun moyen de savoir ce que le moteur a reconnu — la rupture d'⟦A-57⟧, à
// l'identique.
export * from "./niche.js";
// — Nuit, tendances, arbre, boutique, événements, fins, pause annoncée —
export * from "./sleep.js";
export * from "./trends.js";
export * from "./skills.js";
export * from "./boutique.js";
export * from "./events.js";
export * from "./gameover.js";
export * from "./pause.js";
// ⟦A-4 E / M2.10⟧ Le marché des freelances. Exporté en entier : le client en a besoin pour
// l'écran du marché (offres, cotes et surtout **prix**, `devisEmbauches`), le serveur pour
// valider une soumission, `scripts/simulate.ts` pour mesurer.
export * from "./freelances.js";
// — Publication : tout sauf les deux noms en collision (A40) —
export { publier, classifier, scoresEffectifs, slotDuSujet, tauxRenteFige, TREND_MULT_MOTEUR, ACCROCHE_FROID, ACCROCHE_FROID_EVERGREEN, ACCROCHE_SATUREE_ME_TOO, SCORE_CAP_TOLERANCE, CHANCE_BASSE, QUALITE_NULLE, EXECUTION_NULLE, QUALITE_EVERGREEN, QUALITE_LONGUE_REUSSIE, RENTE_BASE, RENTE_PLAFOND, VIRAL_P_BASE, VIRAL_P_BONUS, VIRAL_SEUIL_EXECUTION, VIRAL_CHANCE, MOMENTUM_PUBLICATION, SM_FATIGUE_MIN, SM_FATIGUE_MAX, potentielAccroche as potentielAccrochePublication, RENTE_MULT_SERIE as RENTE_MULT_SERIE_PUBLICATION, } from "./publish.js";
//# sourceMappingURL=index.js.map